/**
 * Error Page Component
 *
 * Displays various error states with user-friendly messages.
 */
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

/**
 * Error types
 */
export type ErrorType =
  | '404'
  | '403'
  | '500'
  | 'network'
  | 'timeout'
  | 'unknown';

/**
 * Error configuration
 */
interface ErrorConfig {
  icon: string;
  title: string;
  description: string;
  suggestion: string;
}

const ERROR_CONFIGS: Record<ErrorType, ErrorConfig> = {
  '404': {
    icon: '🔍',
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
    suggestion: 'Check the URL or navigate back to the dashboard.',
  },
  '403': {
    icon: '🔒',
    title: 'Access Denied',
    description: "You don't have permission to access this resource.",
    suggestion: 'Contact your administrator if you believe this is an error.',
  },
  '500': {
    icon: '💥',
    title: 'Server Error',
    description: 'Something went wrong on our end. Our team has been notified.',
    suggestion: 'Please try again later or contact support.',
  },
  network: {
    icon: '📡',
    title: 'Network Error',
    description: 'Unable to connect to the server. Please check your connection.',
    suggestion: 'Verify your internet connection and try again.',
  },
  timeout: {
    icon: '⏱️',
    title: 'Request Timeout',
    description: 'The request took too long to complete.',
    suggestion: 'The server might be busy. Please try again.',
  },
  unknown: {
    icon: '❓',
    title: 'Unknown Error',
    description: 'An unexpected error occurred.',
    suggestion: 'Please try again or contact support if the problem persists.',
  },
};

/**
 * Error Page Props
 */
interface ErrorPageProps {
  type?: ErrorType;
  title?: string;
  description?: string;
  showBackButton?: boolean;
  showDashboardButton?: boolean;
  showRetryButton?: boolean;
  onRetry?: () => void;
}

/**
 * Error Page Component
 */
export default function ErrorPage({
  type = 'unknown',
  title,
  description,
  showBackButton = true,
  showDashboardButton = true,
  showRetryButton = false,
  onRetry,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const location = useLocation();


  // Check for error info passed via location state
  const locationState = location.state as {
    errorType?: ErrorType;
    errorMessage?: string;
  } | null;

  const errorType = locationState?.errorType || type;
  const errorConfig = ERROR_CONFIGS[errorType];

  const displayTitle = title || errorConfig.title;
  const displayDescription =
    description || locationState?.errorMessage || errorConfig.description;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center pb-2">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">{errorConfig.icon}</span>
          </div>
          <CardTitle className="text-2xl">{displayTitle}</CardTitle>
          <CardDescription className="text-base mt-2">
            {displayDescription}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Suggestion */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-blue-500 text-lg">💡</span>
              <p className="text-sm text-blue-700">{errorConfig.suggestion}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {showRetryButton && onRetry && (
              <Button onClick={onRetry} variant="default">
                Try Again
              </Button>
            )}
            {showBackButton && (
              <Button
                onClick={() => navigate(-1)}
                variant={showRetryButton ? 'outline' : 'default'}
              >
                Go Back
              </Button>
            )}
            {showDashboardButton && (
              <Button variant="outline" asChild>
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            )}
          </div>

          {/* Help links */}
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Need help?{' '}
              <a
                href="mailto:support@agent-mesh.org"
                className="text-primary hover:underline"
              >
                Contact Support
              </a>
              {' or '}
              <a
                href="https://docs.agent-mesh.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View Documentation
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * 404 Not Found Page
 */
export function NotFoundPage() {
  return <ErrorPage type="404" />;
}

/**
 * 403 Forbidden Page
 */
export function ForbiddenPage() {
  return <ErrorPage type="403" />;
}

/**
 * 500 Server Error Page
 */
export function ServerErrorPage() {
  return (
    <ErrorPage
      type="500"
      showRetryButton
      onRetry={() => window.location.reload()}
    />
  );
}

/**
 * Network Error Page
 */
export function NetworkErrorPage({ onRetry }: { onRetry?: () => void }) {
  return (
    <ErrorPage
      type="network"
      showRetryButton
      onRetry={onRetry || (() => window.location.reload())}
    />
  );
}
